import React, { createContext, useContext, useState } from 'react';
import { translations } from '../i18n/translations';

export const LANGUAGES = [
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳', short: 'VI' },
  { code: 'en', label: 'English', flag: '🇺🇸', short: 'EN' },
  { code: 'zh', label: '简体中文', flag: '🇨🇳', short: '中文' }
];

export interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: Record<string, any>) => string;
  languages: typeof LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'vi',
  setLanguage: () => {},
  t: (key: string) => key,
  languages: LANGUAGES
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('huan_dex_lang');
      if (saved && (saved === 'en' || saved === 'vi' || saved === 'zh')) {
        return saved;
      }
      // Auto detect user browser language
      const browserLang = (navigator.language || (navigator as any).userLanguage || '').toLowerCase();
      if (browserLang.startsWith('vi')) return 'vi';
      if (browserLang.startsWith('zh')) return 'zh';
      return 'en';
    } catch (e) {
      return 'en';
    }
  });

  const setLanguage = (lang) => {
    if (lang === 'en' || lang === 'vi' || lang === 'zh') {
      setLanguageState(lang);
      try {
        localStorage.setItem('huan_dex_lang', lang);
      } catch (e) {
        console.error('Could not save language to localStorage', e);
      }
    }
  };

  // Translation helper function
  const t = (key, params = {}) => {
    const langDict = translations[language] || translations.en;
    let str = langDict[key] || translations.en[key] || key;

    // Replace {variable} in translation strings
    if (params && typeof params === 'object') {
      Object.keys(params).forEach((paramKey) => {
        str = str.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), params[paramKey]);
      });
    }

    return str;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
